import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useOnderaannemerPrijzenStore } from '@/stores/onderaannemerPrijzen';
import type { VendorPriceRow, PriceTier } from '@/api/onderaannemerPrijzen';
import { usePricedSystems } from '@/stores/systems';
import type { SystemDef } from '@/api/systems';

/** The card's title colour is the system's dot colour — see @source inline in index.css. */
function accentFromDot(dot: string): string {
  return dot.replace('bg-', 'text-');
}

const euroFmt = new Intl.NumberFormat('nl-BE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatEuro(value: number): string {
  return euroFmt.format(value);
}

/** Parse a Dutch/euro-ish input into a positive number, or null if invalid. */
function parsePrice(raw: string): number | null {
  const normalized = raw.replace(',', '.').trim();
  if (normalized === '') return null;
  const n = Number(normalized);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

export function OnderaannemerPrijzenView() {
  const {
    vendors,
    selectedVendorId,
    loading,
    error,
    toast,
    fetchPrices,
    selectVendor,
    addPrice,
    editPrice,
    removePrice,
    clearToast,
  } = useOnderaannemerPrijzenStore(
    useShallow((s) => ({
      vendors: s.vendors,
      selectedVendorId: s.selectedVendorId,
      loading: s.loading,
      error: s.error,
      toast: s.toast,
      fetchPrices: s.fetchPrices,
      selectVendor: s.selectVendor,
      addPrice: s.addPrice,
      editPrice: s.editPrice,
      removePrice: s.removePrice,
      clearToast: s.clearToast,
    })),
  );

  // Pending delete (confirm dialog). null = closed.
  const [pendingDelete, setPendingDelete] = useState<{
    id: number;
    productId: number;
    tier: PriceTier;
    label: string;
    tierLabel: string;
  } | null>(null);

  // Legpatronen the user manually revealed (by productId) so they can fill a
  // price. Rows with no price are hidden until revealed. Resets per vendor.
  const [revealed, setRevealed] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  useEffect(() => {
    setRevealed(new Set());
  }, [selectedVendorId]);

  const revealLp = (productId: number) => {
    setRevealed((prev) => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
  };

  // Auto-dismiss toast after 4s.
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(clearToast, 4000);
    return () => clearTimeout(timer);
  }, [toast, clearToast]);

  const selectedVendor = useMemo(
    () => vendors.find((v) => v.vendorId === selectedVendorId) ?? null,
    [vendors, selectedVendorId],
  );

  // One card per system that actually has placement variants — the roster (and with it
  // the price grid) is defined in Odoo, so giving a new system its "Plaatsing
  // onderaannemers" variants makes its card appear here with no code change.
  const pricedSystems = usePricedSystems();
  const grouped = useMemo<Array<{ system: SystemDef; rows: VendorPriceRow[] }>>(() => {
    if (!selectedVendor) return [];
    const byCode = new Map<string, VendorPriceRow[]>();
    for (const row of selectedVendor.rows) {
      const code = row.systeem.toLowerCase();
      const bucket = byCode.get(code);
      if (bucket) bucket.push(row);
      else byCode.set(code, [row]);
    }
    const ordered: Array<{ system: SystemDef; rows: VendorPriceRow[] }> = [];
    const seen = new Set<string>();
    for (const system of pricedSystems) {
      seen.add(system.code);
      ordered.push({ system, rows: byCode.get(system.code) ?? [] });
    }
    // A system with price rows that the roster does not know (removed in Odoo, or the
    // roster has not loaded) must still be editable — never hide someone's prices.
    for (const [code, rows] of byCode) {
      if (seen.has(code)) continue;
      ordered.push({
        system: { code, label: code, dot: 'bg-td-muted', kring: false, collector: false, priced: true, billingPriced: true },
        rows,
      });
    }
    return ordered.map(({ system, rows }) => ({
      system,
      rows: [...rows].sort((a, b) => a.legpatroon - b.legpatroon),
    }));
  }, [selectedVendor, pricedSystems]);

  const handleVendorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(e.target.value);
    selectVendor(value > 0 ? value : null);
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    removePrice(pendingDelete.id, pendingDelete.productId, pendingDelete.tier);
    setPendingDelete(null);
  };

  const onRequestDelete = (row: VendorPriceRow, tier: PriceTier) => {
    const id = tier === 'under200' ? row.idUnder200 : row.idFrom200;
    if (id == null) return;
    setPendingDelete({
      id,
      productId: row.productId,
      tier,
      label: row.label,
      tierLabel: tier === 'under200' ? '< 200 m²' : '≥ 200 m²',
    });
  };

  return (
    <div className="px-4 lg:px-8 py-6 max-w-4xl">
      {/* Header */}
      <h1 className="text-xl font-extrabold text-td-navy">OA Prijzen</h1>
      <p className="text-sm text-td-muted mt-0.5">
        Plaatsingsprijzen per onderaannemer, per legpatroon en staffel (€/m²)
      </p>

      {/* Vendor selector */}
      <div className="mt-5">
        <label
          htmlFor="oa-prijzen-vendor"
          className="block text-xs font-semibold text-td-muted uppercase tracking-wide mb-1.5"
        >
          Onderaannemer
        </label>
        <select
          id="oa-prijzen-vendor"
          aria-label="Selecteer onderaannemer"
          value={selectedVendorId ?? ''}
          onChange={handleVendorChange}
          className="w-full sm:w-80 px-3 py-2.5 text-sm font-medium text-td-navy border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue focus:border-transparent bg-white shadow-sm"
        >
          <option value="">Kies een onderaannemer…</option>
          {vendors.map((v) => (
            <option key={v.vendorId} value={v.vendorId}>
              {v.vendorName}
            </option>
          ))}
        </select>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mt-4 bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
          <p className="text-sm font-semibold whitespace-pre-line">{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <div
            className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin"
            role="status"
          >
            <span className="sr-only">Laden…</span>
          </div>
          <p className="text-td-muted text-sm mt-3">Prijzen laden…</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && !selectedVendor && (
        <div className="mt-6 border border-dashed border-gray-300 rounded-2xl text-center py-16 bg-gray-50/50">
          <p className="text-td-muted text-sm">
            Selecteer een onderaannemer om de prijzen te bekijken.
          </p>
        </div>
      )}

      {/* Price cards — one per systeem */}
      {!loading && selectedVendor && (
        <div className="mt-6 space-y-5">
          {grouped.map(({ system, rows }) => (
            <SystemCard
              key={system.code}
              system={system}
              rows={rows}
              revealed={revealed}
              onRevealLp={revealLp}
              onAdd={addPrice}
              onEdit={editPrice}
              onRequestDelete={onRequestDelete}
            />
          ))}
        </div>
      )}

      {/* Delete confirm dialog */}
      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPendingDelete(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="oa-prijs-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <h3 id="oa-prijs-delete-title" className="text-lg font-extrabold text-td-navy">
                Prijs verwijderen?
              </h3>
              <p className="text-sm text-td-muted mt-2">
                Verwijder de prijs voor <span className="font-semibold text-td-navy">{pendingDelete.label}</span>{' '}
                ({pendingDelete.tierLabel}). Dit kan niet ongedaan gemaakt worden.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 pb-6">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                className="px-4 py-2 text-sm font-semibold bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition"
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-2 rounded-lg shadow-lg text-white flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2 ${
            toast.kind === 'success' ? 'bg-emerald-600' : 'bg-red-600'
          }`}
          role="status"
        >
          <span className="text-sm font-semibold">{toast.message}</span>
        </div>
      )}
    </div>
  );
}

interface SystemCardProps {
  system: SystemDef;
  rows: VendorPriceRow[];
  revealed: Set<number>;
  onRevealLp: (productId: number) => void;
  onAdd: (productId: number, tier: PriceTier, price: number) => void;
  onEdit: (id: number, productId: number, tier: PriceTier, price: number) => void;
  onRequestDelete: (row: VendorPriceRow, tier: PriceTier) => void;
}

/** A row is shown when it carries a price or was manually revealed. */
function isVisibleRow(row: VendorPriceRow, revealed: Set<number>): boolean {
  return row.priceUnder200 != null || row.priceFrom200 != null || revealed.has(row.productId);
}

function SystemCard({
  system,
  rows,
  revealed,
  onRevealLp,
  onAdd,
  onEdit,
  onRequestDelete,
}: SystemCardProps) {
  const visibleRows = rows.filter((r) => isVisibleRow(r, revealed));
  const hiddenRows = rows.filter((r) => !isVisibleRow(r, revealed));

  const handleAddLp = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const productId = Number(e.target.value);
    if (productId > 0) onRevealLp(productId);
    e.target.value = '';
  };

  return (
    <section className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Card header */}
      <div className="flex items-center gap-2.5 px-4 sm:px-5 py-3 border-b border-gray-100">
        <span className={`w-2.5 h-2.5 rounded-full ${system.dot}`} aria-hidden="true" />
        <h2 className={`text-sm font-extrabold uppercase tracking-wide ${accentFromDot(system.dot)}`}>
          {system.label}
        </h2>
        <span className="text-xs text-td-light">
          · {visibleRows.length} {visibleRows.length === 1 ? 'legpatroon' : 'legpatronen'}
        </span>
      </div>

      {/* Column headers — only when there are rows to align */}
      {visibleRows.length > 0 && (
        <div className="grid grid-cols-[minmax(5rem,1fr)_7.5rem_7.5rem] sm:grid-cols-[minmax(8rem,1fr)_9rem_9rem] items-center px-4 sm:px-5 py-2 bg-gray-50/70 text-[11px] font-bold text-td-muted uppercase tracking-wide">
          <span>Legpatroon</span>
          <span className="text-right">Tot 200 m²</span>
          <span className="text-right">Vanaf 200 m²</span>
        </div>
      )}

      {/* Rows */}
      <div className="divide-y divide-gray-100">
        {visibleRows.map((row) => (
          <div
            key={row.productId}
            className="grid grid-cols-[minmax(5rem,1fr)_7.5rem_7.5rem] sm:grid-cols-[minmax(8rem,1fr)_9rem_9rem] items-center px-4 sm:px-5 py-2.5 hover:bg-gray-50/60 transition-colors"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="shrink-0 inline-flex items-center justify-center min-w-[2.75rem] px-2 py-0.5 rounded-md bg-gray-100 text-xs font-bold text-td-navy">
                LP {row.legpatroon}
              </span>
              {/* Full variant label kept for accessibility / screen readers */}
              <span className="sr-only">{row.label}</span>
            </div>
            <div className="flex justify-end">
              <PriceCell
                row={row}
                tier="under200"
                price={row.priceUnder200}
                id={row.idUnder200}
                onAdd={onAdd}
                onEdit={onEdit}
                onRequestDelete={onRequestDelete}
              />
            </div>
            <div className="flex justify-end">
              <PriceCell
                row={row}
                tier="from200"
                price={row.priceFrom200}
                id={row.idFrom200}
                onAdd={onAdd}
                onEdit={onEdit}
                onRequestDelete={onRequestDelete}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Empty hint + add-legpatroon picker */}
      <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-t border-gray-100 bg-gray-50/40">
        {visibleRows.length === 0 ? (
          <span className="text-xs text-td-light">Nog geen legpatronen — voeg er één toe.</span>
        ) : (
          <span />
        )}
        {hiddenRows.length > 0 && (
          <select
            aria-label={`Legpatroon toevoegen voor ${system.label}`}
            value=""
            onChange={handleAddLp}
            className="text-xs font-semibold text-td-blue border border-dashed border-gray-300 rounded-lg px-2.5 py-1.5 bg-white hover:border-td-blue focus:outline-none focus:ring-2 focus:ring-td-blue/40 cursor-pointer"
          >
            <option value="">+ Legpatroon toevoegen</option>
            {hiddenRows.map((r) => (
              <option key={r.productId} value={r.productId}>
                LP {r.legpatroon}
              </option>
            ))}
          </select>
        )}
      </div>
    </section>
  );
}

interface PriceCellProps {
  row: VendorPriceRow;
  tier: PriceTier;
  price: number | null;
  id: number | null;
  onAdd: (productId: number, tier: PriceTier, price: number) => void;
  onEdit: (id: number, productId: number, tier: PriceTier, price: number) => void;
  onRequestDelete: (row: VendorPriceRow, tier: PriceTier) => void;
}

function PriceCell({ row, tier, price, id, onAdd, onEdit, onRequestDelete }: PriceCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const tierLabel = tier === 'under200' ? '< 200 m²' : '≥ 200 m²';

  const startEdit = () => {
    setDraft(price != null ? String(price).replace('.', ',') : '');
    setEditing(true);
  };

  const commit = () => {
    const parsed = parsePrice(draft);
    setEditing(false);
    if (parsed == null) return; // invalid → block, keep existing value
    if (id != null) {
      if (parsed !== price) onEdit(id, row.productId, tier, parsed);
    } else {
      onAdd(row.productId, tier, parsed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className="relative">
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-td-light">
          €
        </span>
        <input
          autoFocus
          type="text"
          inputMode="decimal"
          value={draft}
          aria-label={`Prijs ${row.label} ${tierLabel}`}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          className="w-24 pl-6 pr-2 py-1.5 text-sm text-right font-semibold text-td-navy border border-td-blue rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue/40"
        />
      </div>
    );
  }

  if (price == null) {
    return (
      <button
        type="button"
        onClick={startEdit}
        className="inline-flex items-center px-2.5 py-1.5 rounded-lg border border-dashed border-gray-300 text-xs font-semibold text-td-light hover:border-td-blue hover:text-td-blue transition-colors"
      >
        + toevoegen
      </button>
    );
  }

  return (
    <span className="group inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={startEdit}
        className="inline-flex items-baseline gap-1 px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-200 hover:border-td-blue hover:bg-td-blue/5 transition-colors"
        aria-label={`Bewerk prijs ${row.label} ${tierLabel}`}
      >
        <span className="text-base font-bold text-td-navy tabular-nums">{formatEuro(price)}</span>
        <span className="text-[10px] font-medium text-td-light">/m²</span>
      </button>
      <button
        type="button"
        onClick={() => onRequestDelete(row, tier)}
        disabled={!id}
        className="text-gray-300 hover:text-red-600 transition-all text-sm leading-none opacity-0 group-hover:opacity-100 focus:opacity-100 disabled:opacity-0 disabled:cursor-not-allowed"
        aria-label={`Verwijder prijs ${row.label} ${tierLabel}`}
        title="Verwijderen"
      >
        ✕
      </button>
    </span>
  );
}
